import React, { useEffect, useState } from 'react';
import { feedbackAPI, uploadAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import SubscriptionGate from '../../components/SubscriptionGate';
import HLSPlayer from '../../components/HLSPlayer';
import VideoSourcePicker from '../../components/VideoSourcePicker';
import { motion } from 'framer-motion';
import { Loader2, Video, Clock, CheckCircle2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function CoachFeedback() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [notes, setNotes] = useState('');
  const [videoFilename, setVideoFilename] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeedbacks();
  }, []);

  const loadFeedbacks = async () => {
    try {
      const res = await feedbackAPI.list();
      setFeedbacks(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleVideoFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await uploadAPI.video(file, (e) => {
        setUploadProgress(Math.round((e.loaded * 100) / e.total));
      });
      setVideoFilename(res.data.filename);
      toast.success('Video uploaded!');
    } catch (err) {
      toast.error(err.message || 'Upload failed — check your connection');
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const handleSubmit = async () => {
    if (!videoFilename && !notes) {
      toast.error('Upload a video or add notes');
      return;
    }
    setSubmitting(true);
    try {
      await feedbackAPI.create({ video_filename: videoFilename, notes });
      toast.success('Submitted for coach review!');
      setNotes('');
      setVideoFilename('');
      loadFeedbacks();
    } catch {
      toast.error('Failed to submit');
    }
    setSubmitting(false);
  };

  return (
    <Layout title="COACH FEEDBACK">
      <SubscriptionGate feature="feedback">
        <div className="px-4 py-6 space-y-6">
          {/* Upload Section */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-[#E2E8F0] rounded-xl p-5"
          >
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold tracking-tight mb-1" data-testid="feedback-upload-title">
              SUBMIT FOR REVIEW
            </h3>
            <p className="text-xs text-[#64748B] mb-4">
              Upload a video of your rep. Coach review in 24-48 hours.
            </p>

            {/* Video Upload */}
            <div className="mb-4">
              {uploading ? (
                <div className="border-2 border-dashed border-[#2563EB] rounded-xl p-6 text-center" data-testid="feedback-upload-progress">
                  <Loader2 size={24} className="animate-spin mx-auto text-[#2563EB]" />
                  <p className="text-sm text-[#64748B] mt-2">Uploading {uploadProgress}%</p>
                  <div className="w-full bg-[#E2E8F0] rounded-full h-1.5 mt-3">
                    <div className="bg-[#2563EB] h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              ) : videoFilename ? (
                <div className="border-2 border-[#10B981]/40 bg-[#10B981]/5 rounded-xl p-4 text-center mb-3" data-testid="feedback-video-ready">
                  <CheckCircle2 size={20} className="mx-auto text-[#10B981]" />
                  <p className="text-sm text-[#10B981] font-medium mt-1">Video ready</p>
                  <button
                    onClick={() => setVideoFilename('')}
                    className="text-xs text-[#64748B] underline mt-1 hover:text-[#0A2242]"
                    data-testid="feedback-video-clear"
                  >
                    Replace
                  </button>
                </div>
              ) : (
                <VideoSourcePicker onFileSelected={handleVideoFile} disabled={uploading} />
              )}
            </div>

            {/* Notes */}
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent placeholder:text-[#64748B] transition-all resize-none h-24"
              placeholder="Any notes for the coach? (optional)"
              data-testid="feedback-notes"
            />

            <button
              onClick={handleSubmit}
              disabled={submitting || (!videoFilename && !notes)}
              className="w-full mt-4 bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg px-8 py-4 transition-colors disabled:opacity-30 flex items-center justify-center gap-2"
              data-testid="feedback-submit-btn"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <Video size={18} />}
              {submitting ? 'Submitting...' : 'Submit for Review'}
            </button>
          </motion.div>

          {/* Previous Feedback */}
          {loading ? (
            <div className="flex justify-center py-8"><div className="spinner" /></div>
          ) : feedbacks.length > 0 ? (
            <div className="space-y-3">
              <h3 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight">YOUR SUBMISSIONS</h3>
              {feedbacks.map((fb, i) => (
                <motion.div
                  key={fb.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white border border-[#E2E8F0] rounded-xl p-4"
                  data-testid={`feedback-item-${i}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {fb.video_filename && <Video size={14} className="text-[#2563EB]" />}
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                        fb.status === 'reviewed'
                          ? 'bg-[#10B981]/15 text-[#10B981]'
                          : 'bg-[#FF9100]/15 text-[#FF9100]'
                      }`}>
                        {fb.status === 'reviewed' ? 'Reviewed' : 'Pending'}
                      </span>
                    </div>
                    <span className="text-[10px] text-[#64748B] flex items-center gap-1">
                      <Clock size={10} />
                      {new Date(fb.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {fb.video_filename && (
                    <div className="my-2">
                      <HLSPlayer videoFilename={fb.video_filename} className="w-full rounded-lg" />
                    </div>
                  )}
                  {fb.notes && <p className="text-sm text-[#64748B] mb-2">{fb.notes}</p>}
                  {fb.coach_response && (
                    <div className="bg-[#F8FAFC] rounded-lg p-3 mt-2 border border-[#E2E8F0]">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare size={12} className="text-[#00E5FF]" />
                        <span className="text-[10px] text-[#00E5FF] font-bold uppercase tracking-widest">Coach</span>
                      </div>
                      <p className="text-sm text-[#64748B]">{fb.coach_response}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          ) : null}
        </div>
      </SubscriptionGate>
    </Layout>
  );
}
